import { Link } from 'react-router-dom'
import { Inbox } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { fetchStudioAsks, STUDIO_ASK_CAPABILITIES, STUDIO_ASKS_KEY } from '../api/asks'
import { fetchContent, fill, text } from '../api/content'
import { useCan } from '../useSession'

/**
 * What is waiting on an answer from the studio (D-324).
 *
 * **Surfaced, not navigated to** — owner's call, and still right about the
 * *count*: a request waiting on the studio is something that happened, not a
 * place (D-103, D-167).
 *
 * **What changed underneath it** (D-560): Requests is a destination now,
 * because `/asks` became the one home for eight kinds of ask and the only
 * door to assigning a task - somewhere you go on purpose. This chip is still
 * the thing that shouts, and it still disappears at zero.
 *
 * **The conflict chip that stood beside this one is gone** (D-593). It was
 * put here when clashes had no place of their own; D-585 gave them a rail
 * destination with a badge, and `asked`: *"with conflicts having its own nav
 * location now, lets get rid of the bubble that shows on all pages, you can
 * see the numbers on the conflicts tab on the left now."* The request chip
 * stays because Requests has no badge and cannot have one: the bell already
 * carries `Ask` notifications, and D-324's rule is that nothing is counted on
 * two surfaces.
 *
 * **It disappears when there is nothing.** That is the point of putting it here
 * rather than on a bar: a permanent row saying zero is furniture, and a row that
 * is sometimes empty is a control that lies. This is only ever the one count
 * and only ever when it is not zero.
 *
 * **One chip where there were four** (D-543). Long bookings, held makeups and
 * unsettled makeups were three queues behind three capabilities with three
 * chips; they are one list now, and this counts what that list shows — owed,
 * late, or only chaseable — so the chip and the screen cannot disagree.
 */
export default function NeedsADecision() {
  /*
   * Any one of these reads the studio's list. Checked here as well as on the
   * server because the list carries a guardian's name and number on every
   * chase row, and a request that will be refused is still a request for a
   * directory of families.
   */
  const reads = STUDIO_ASK_CAPABILITIES.map(useCan).some(Boolean)

  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  const { data: asks } = useQuery({
    queryKey: STUDIO_ASKS_KEY,
    queryFn: fetchStudioAsks,
    staleTime: 60_000,
    enabled: reads,
  })

  const waiting = asks?.length ?? 0

  if (waiting === 0) return null

  /*
   * The accent, not `warn` (D-388).
   *
   * Reported: *"this should be more obvious. maybe a hot pink or something so
   * it catches the eye."* Fair — a long booking waiting on Tonya was an
   * outlined chip in body colour, indistinguishable from the tool links beside
   * it, and **nothing else tells her it is there**: this component renders
   * nothing at all when the count is zero, so if it is missed here it is missed
   * entirely. Nothing is broken; somebody is owed an answer — which is the
   * distinction the accent already draws between "attend to this" and "this
   * is broken".
   */
  return (
    <div className="mb-4 flex flex-wrap gap-2">
      <Link
        to="/asks"
        className="flex items-center gap-2 rounded-xl border border-transparent bg-accent px-3 py-2 text-[13px] font-semibold text-on-accent no-underline transition-colors hover:brightness-110"
      >
        <Inbox size={15} />
        {waiting === 1
          ? text(content, 'asks.chip.one', 'One request needs an answer')
          : fill(content, 'asks.chip.many', { count: waiting }, '{{count}} requests need an answer')}
      </Link>
    </div>
  )
}
